import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Loader2, ArrowLeft } from 'lucide-react';
import { portalPost, setPortalToken } from '../../lib/portalApi';

export default function PortalLogin() {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [view, setView] = useState('login'); // 'login' | 'forgot' | 'sent'
  const [forgotEmail, setForgotEmail] = useState('');

  const submit = async (e) => {
    e.preventDefault();
    if (!email.trim() || !password) return toast.error('Enter your email and password');
    setLoading(true);
    try {
      const res = await portalPost('/portal/login', { email: email.trim(), password });
      setPortalToken(res.access_token);
      navigate('/portal', { replace: true });
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Invalid email or password');
    } finally {
      setLoading(false);
    }
  };

  const submitForgot = async (e) => {
    e.preventDefault();
    if (!forgotEmail.trim()) return toast.error('Enter your email address');
    setLoading(true);
    try {
      await portalPost('/portal/forgot-password', { email: forgotEmail.trim() });
      setView('sent');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-white">
      {/* Left brand panel */}
      <div className="hidden lg:flex flex-col justify-between p-10 bg-gradient-to-b from-[#0b2f2b] via-[#14504E] to-[#1F7A6E] text-white">
        <img src="/assets/elr-logo-white.png" alt="ELR Industries" className="h-9 w-auto self-start object-contain" />
        <div>
          <div className="text-xs font-display font-bold uppercase tracking-[0.2em] text-white/60 mb-2">Customer Portal</div>
          <h1 className="font-display text-3xl font-extrabold leading-tight mb-3">Track your concrete,<br />place your next order.</h1>
          <p className="text-sm text-white/70 max-w-sm">See every delivery to your site, download signed delivery tickets, and request a new pour — all in one place.</p>
        </div>
        <div className="text-xs text-white/40">ELR Industries Pvt Ltd</div>
      </div>

      {/* Right form panel */}
      <div className="flex items-center justify-center p-8">
        <div className="w-full max-w-sm portal-form-animate">

          {/* Mobile logo */}
          <img
            src="/assets/elr-logo-teal.png"
            alt="ELR Industries"
            className="block h-8 w-auto object-contain mb-6 lg:hidden"
          />

          {/* ── Sign in ── */}
          {view === 'login' && (
            <form onSubmit={submit} className="space-y-4">
              <h2 className="font-display text-2xl font-extrabold text-slate-900">Sign in to your account</h2>
              <p className="text-sm text-slate-500">Use the login your ELR sales contact set up for you.</p>
              <div>
                <label className="label">Email</label>
                <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="input" placeholder="you@company.com" autoFocus data-testid="portal-login-email" />
              </div>
              <div>
                <label className="label">Password</label>
                <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} className="input" data-testid="portal-login-password" />
                <button
                  type="button"
                  onClick={() => { setForgotEmail(email); setView('forgot'); }}
                  className="text-xs text-[#1F7A6E] hover:underline mt-1.5 float-right"
                  data-testid="portal-forgot-password-link"
                >
                  Forgot password?
                </button>
              </div>
              <div className="pt-1">
                <button type="submit" disabled={loading} className="btn-primary w-full justify-center" data-testid="portal-login-submit">
                  {loading ? <Loader2 size={14} className="animate-spin" /> : 'Sign In'}
                </button>
              </div>
              <p className="text-xs text-slate-400">No login yet? Contact your ELR Industries sales representative.</p>
            </form>
          )}

          {/* ── Forgot password ── */}
          {view === 'forgot' && (
            <form onSubmit={submitForgot} className="space-y-4">
              <button
                type="button"
                onClick={() => setView('login')}
                className="flex items-center gap-1 text-sm text-slate-500 hover:text-slate-800 mb-2"
                data-testid="portal-forgot-back-btn"
              >
                <ArrowLeft size={14} /> Back to sign in
              </button>
              <h2 className="font-display text-2xl font-extrabold text-slate-900">Reset your password</h2>
              <p className="text-sm text-slate-500">Enter your account email and we'll send you a reset link valid for 1 hour.</p>
              <div>
                <label className="label">Email</label>
                <input
                  type="email"
                  value={forgotEmail}
                  onChange={(e) => setForgotEmail(e.target.value)}
                  className="input"
                  placeholder="you@company.com"
                  autoFocus
                  data-testid="portal-forgot-email-input"
                />
              </div>
              <button type="submit" disabled={loading} className="btn-primary w-full justify-center" data-testid="portal-forgot-submit">
                {loading ? <Loader2 size={14} className="animate-spin" /> : 'Send Reset Link'}
              </button>
            </form>
          )}

          {/* ── Email sent ── */}
          {view === 'sent' && (
            <div className="space-y-4 text-center" data-testid="portal-forgot-sent">
              <div className="w-14 h-14 bg-[#1F7A6E]/10 rounded-full flex items-center justify-center mx-auto">
                <svg className="w-7 h-7 text-[#1F7A6E]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
              </div>
              <h2 className="font-display text-2xl font-extrabold text-slate-900">Check your inbox</h2>
              <p className="text-sm text-slate-500">If <strong>{forgotEmail}</strong> is registered, a password reset link has been sent. It expires in 1 hour.</p>
              <button
                type="button"
                onClick={() => setView('login')}
                className="btn-primary w-full justify-center"
                data-testid="portal-back-to-login-btn"
              >
                Back to Sign In
              </button>
              <p className="text-xs text-slate-400">Didn't receive it? Check your spam folder or contact your ELR sales representative.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

